import React, { Ref, forwardRef, useEffect, useRef } from "react";
import { useState, useContext } from "react";
import { Button, Card, Table, Modal, Alert, Form, InputGroup } from "react-bootstrap";
import { getDataReader, RPWDataReader, } from "../../livedataFuncs";
import { useAppSelector } from "../../redux/hooks";
import { lddStateObject } from "../../redux/reducer";
import { Loading, LoadingIcon } from "../Loading";
import { lddDriveObj, FileDrive_Funcs, hasSpecialCharacter } from "./filedriveFuncs"
import { FolderEntry, FileSystem, FileEntry } from "./filebase";
import { StateTypeContext } from "../clientportal/types";
import { ActivityCenterContext } from "../../Context/ActivityCenter";
import DocumentRow from "./DocumentRow";
import FolderRow from "./FolderRow";

export const folderSpecialChars: string[] = ["|", ":", "?", "*", "%", "#", "\\", "/", "<", ">", ".", "{", "}", "\"", "`"];
const fileSpecialChars: string[] = ["|", ":", "?", "*", "%", "#", "\\", "/", "<", ">", ".", "{", "}", "\"", "`"];

export const FileDrive = forwardRef(({ }, ref: Ref<HTMLDivElement>) => {
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    let DataReaderObj: RPWDataReader;
    DataReaderObj = getDataReader(livedataRootContainer.dataRoot);
    const activityCenter = useContext(ActivityCenterContext);

    const [isDriveLoading, setIsDriveLoading] = useState<boolean>(false);
    const [FolderContent, setFolderContent] = useState<Array<FileEntry | FolderEntry>>([]);
    const [Drive, setDrive] = useState<FileSystem>(undefined);
    const [loadingDrive, setLoadingDrive] = useState<boolean>(false);
    const SessionID = useAppSelector((state) => state.SessionID);
    const AppCode = useAppSelector((state) => state.SessionInfo.appCode)
    const matterNumber = DataReaderObj.getFileNumber();
    const AppID = DataReaderObj.getAppId();
    const PortalID = DataReaderObj.getClientConnectId();

    const portalState: StateTypeContext = { appCode: AppCode, appId: AppID, page: -1, portalId: PortalID, processor: null };
    const config: lddDriveObj = {
        type: 1,
        sharepointConfig: {
            rootDrive: "ldd_fake_drive_root",
            host: "ldd_fake_drive_tenant",
            urlOverwrite: `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/filedrive/sendToSharepoint?SessionID=${SessionID}`
        }
    }

    const ActivateDrive = async (): Promise<void> => {
        let drive: FileSystem = FileDrive_Funcs.createLddDrive(config, matterNumber, setIsDriveLoading, setFolderContent, activityCenter);
        await FileDrive_Funcs.getMainFolder();
        setDrive(drive);
        setLoadingDrive(false)
    }

    if (!loadingDrive && Drive === undefined) { // don't want to fire twice 
        setLoadingDrive(true)
        ActivateDrive();
    }

    const FormSubmitRef = useRef(null);
    const [validated, setValidated] = useState<boolean>(false);
    const [validatedToProcess, setValidatedToProcess] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const checkFileSize = (file): boolean => {
        const MAX_FILE_SIZE = 25 * Math.pow(2, 20); // 25 MB
        if (file.size > MAX_FILE_SIZE) {
            setErrorMsg("This file exceeds the upload limit of 25MB. Please try another file.");
            setValidated(false);
            return false;
        }
        return true;
    }

    const checkFileName = (name: string): boolean => {
        if (hasSpecialCharacter(name, fileSpecialChars)) {
            setValidated(false);
            setErrorMsg(`Document name may not contain special characters eg. ${fileSpecialChars.join("")}.`);
            setValidatedToProcess(false);
            return false;
        }
        return true;
    }

    const checkFolderName = (name: string): boolean => {
        if (hasSpecialCharacter(name, folderSpecialChars)) {
            setValidated(false);
            setErrorMsg(`Folder name may not contain special characters eg. ${folderSpecialChars.join("")}.`);
            setValidatedToProcess(false);
            return false;
        }
        return true;
    }

    const handleGenericReset = () => {
        setValidated(false);
        setErrorMsg(null);
    }

    const [deleteModal, setDeleteModal] = useState<{
        show: boolean,
        folder?: FolderEntry,
        doc?: FileEntry,
    }>({ show: false });
    const ProccessDelete = async (item: "Folder" | "Document"): Promise<void> => {
        try {
            setIsProcessing(true);
            if (item === "Document") {
                await FileDrive_Funcs.deleteDocument(deleteModal.doc);
            } else {
                await FileDrive_Funcs.deleteFolder(deleteModal.folder);
            }
            setIsProcessing(false);
            setDeleteModal({ show: false, folder: null, doc: null });
        } catch (e) {
            setErrorMsg(e);
            setIsProcessing(false);
        }
    }

    const [renameModal, setRenameModal] = useState<{
        show: boolean,
        folder?: FolderEntry,
        doc?: FileEntry,
    }>({ show: false });
    const handleRenameSubmit = (event, item: "Folder" | "Document") => {
        event.preventDefault();
        event.stopPropagation();
        const form = event.currentTarget;
        if (form.checkValidity() && validated) {
            const newName = form.renameNewName.value;
            ProcessRename(newName.trim(), item === "Document" ? "Docuemnt" : "Folder");
        }
    }

    const ProcessRename = async (newName: string, item: "Folder" | "Docuemnt"): Promise<void> => {
        try {
            setIsProcessing(true);
            const Folder = FileDrive_Funcs.returnCurrentFolder();
            let activityMsg = "";
            if (item === "Docuemnt") {
                const currentDocName = renameModal.doc.name;
                const newFullName = `${newName}.${renameModal.doc.ext}`;
                activityMsg = `Document '${currentDocName}' renamed to '${newFullName}'`;
                await renameModal.doc.moveTo(Folder, newFullName);
            } else {
                const currentFolderName = renameModal.folder.name;
                await renameModal.folder.moveTo(Folder, newName);
                activityMsg = `Folder '${currentFolderName}' renamed to '${newName}'`;
            }
            setIsProcessing(false);
            setRenameModal({ show: false, folder: null, doc: null });
            activityCenter.Add(activityMsg, null, "success", null);
            FileDrive_Funcs.refreshFolder();
            handleGenericReset();
        } catch (e) {
            if (e === "conflict") {
                setErrorMsg(`A ${item === "Docuemnt" ? "file" : "folder "} with this name already exists. Please try a different name.`)
            } else {
                setErrorMsg(e);
            }
            setIsProcessing(false);
        }
    }

    const handleRenameChange = (event, item: "Folder" | "Document") => {
        setValidated(true);
        setErrorMsg(null);
        const form = event.currentTarget;
        if (!form.checkValidity()) {
            return;
        }
        const name = event.currentTarget.renameNewName.value;
        if (item === "Folder") {
            checkFolderName(name);
        } else {
            checkFileName(name);
        }
    }

    const [uploadFile, setUploadFile] = useState<File>(null);
    const [uploadFileFullName, setUploadFileFullName] = useState<string>(null);
    const [uploadNewDocOverwritePop, setUploadNewDocOverwritePop] = useState<boolean>(false);
    const [uploadNewDocPop, setUploadNewDocPop] = useState<boolean>(false);
    const onFileUploadDragEnd = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const myFile = event.dataTransfer.files[0];
        if (myFile.type === "") {
            // invalid file type
            return;
        }
        setUploadFileFullName(myFile.name);
        setUploadFile(myFile);
        setUploadNewDocPop(true);

        setTimeout(() => {  // let the form load first, then start checking
            if (!checkFileSize(myFile)) return;

            setValidated(true);
            setErrorMsg(null);
            setValidatedToProcess(true);
            if (FormSubmitRef.current) {
                FormSubmitRef.current.checkValidity();
            }
            checkFileName(myFile.name.substring(0, myFile.name.indexOf(".")));
        }, 500);
    }

    const uploadNewDocPopSubmit = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const form = event.currentTarget;
        if (form.checkValidity() && validated) {
            ProcessUploadDocument();
        }
    };

    const ProcessUploadDocument = async (): Promise<void> => {
        try {
            setIsProcessing(true);
            try {
                const Folder = FileDrive_Funcs.returnCurrentFolder();
                await Folder.getFile(uploadFileFullName);
                // if continue, means a doc with the same name existed in the folder
                // need to confirm overwriting
                setUploadNewDocOverwritePop(true);
            } catch (e) {
                // no confilct file detect, good to continue uploding
                ProcessUploadDocumentStep2();
            }
        } catch (e) {
            setErrorMsg(e);
            setIsProcessing(false);
        }
    }

    const ProcessUploadDocumentStep2 = async (): Promise<void> => {
        try {
            const Folder = FileDrive_Funcs.returnCurrentFolder();
            await Folder.getFile(uploadFileFullName, { create: true, overwrite: true }, uploadFile);
            setIsProcessing(false);
            setUploadNewDocPop(false);
            handleUploadReset();
            activityCenter.Add(`Document '${uploadFileFullName}' uploaded.`, null, "success", null);
            FileDrive_Funcs.refreshFolder();
        } catch (e) {
            setErrorMsg(e);
            setIsProcessing(false);
        }
    }

    const uploadNewDocPopChange = (e) => {
        setValidated(true);
        setErrorMsg(null);
        setValidatedToProcess(true);

        if (!checkFileSize(uploadFile ? uploadFile : ((e.target) as any).files[0])) {
            setValidated(false);
            setValidatedToProcess(false);
            return;
        }

        const form = e.currentTarget;
        if (!form.checkValidity()) {
            setValidatedToProcess(false);
            return;
        }

        let filename = "";
        if (uploadFile) {
            filename = e.currentTarget.fileToUploadName?.value;
            const docExt = e.currentTarget.fileToUploadExt?.value;
            setUploadFileFullName(`${filename}.${docExt}`);
        } else {
            const name: string = ((e.target) as any).files[0].name;
            setUploadFileFullName(name);
            filename = name.substring(0, name.indexOf("."));
        }
        checkFileName(filename);
    }

    const handleUploadReset = () => {
        setValidated(false);
        setErrorMsg(null);
        setUploadFile(null);
        setUploadFileFullName(null);
    }

    const [createNewFolderPop, setCreateNewFolderPop] = useState<boolean>(false);
    const handleNewFolderSubmit = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const form = event.currentTarget;
        if (form.checkValidity() && validated) {
            const folderName = form.createNewSubFolderName.value;
            ProcessNewSubfolder(folderName.trim());
        }
    }

    const ProcessNewSubfolder = async (folderName: string): Promise<void> => {
        try {
            setIsProcessing(true);
            let Folder = FileDrive_Funcs.returnCurrentFolder();
            await Folder.getFolder(folderName, { create: true })
            setIsProcessing(false);
            setCreateNewFolderPop(false);
            activityCenter.Add(`Folder '${folderName}' created.`, null, "success", null);
            FileDrive_Funcs.refreshFolder();
            handleGenericReset();
        } catch (e) {
            setErrorMsg(e);
            setIsProcessing(false);
        }
    }

    const handleNewFolderChange = (e) => {
        setValidated(true);
        setErrorMsg(null);
        const form = e.currentTarget;
        if (!form.checkValidity()) {
            return;
        }
        const name = e.currentTarget.createNewSubFolderName.value;
        checkFolderName(name);
    }

    const [copyFilePop, setCopyFilePop] = useState<{ show: boolean, doc: FileEntry }>({ show: false, doc: null });
    const handleCopyDocumentSubmit = (event) => {
        const form = event.currentTarget;
        let v = true;
        var newFileName = form.copyFileNewName.value;
        setErrorMsg(null)
        setValidated(false);
        if (form.checkValidity() === false) {
            v = false;
        }

        v = checkFileName(newFileName);

        event.preventDefault();
        event.stopPropagation();
        if (v) {
            ProcessCopyDocument(newFileName.trim());
        }
    }

    const ProcessCopyDocument = async (newFileName: string): Promise<void> => {
        try {
            setValidated(true);
            setIsProcessing(true);
            const newFullName = `${newFileName}.${copyFilePop.doc.ext}`;
            await copyFilePop.doc.copyTo(null, newFullName);
            setIsProcessing(false);
            setCopyFilePop({ show: false, doc: null });
            setErrorMsg(null);
            activityCenter.Add(`New file '${newFullName}' created successfully.`, null, "success", null);
            FileDrive_Funcs.refreshFolder();
        } catch (e) {
            if (e === "conflict") {
                setErrorMsg(`A file with this name already exists. Please try a different name.`)
            } else {
                setErrorMsg(e);
            }
            setIsProcessing(false);
        }
    }

    const [moveFilePop, setMoveFilePop] = useState<{ show: boolean, doc: FileEntry }>({ show: false, doc: null });
    const handleMoveDocumentSubmit = (event) => {
        const form = event.currentTarget;
        var folderName = form.moveFileNewFolder.value;
        setErrorMsg(null)
        event.preventDefault();
        event.stopPropagation();
        ProcessMoveDocument(folderName);
    }

    const ProcessMoveDocument = async (folderName): Promise<void> => {
        try {
            setIsProcessing(true);
            let FileToMove = moveFilePop.doc;

            let CurrentFolder = FileDrive_Funcs.returnCurrentFolder();
            let newFolder: FolderEntry;
            if (folderName === "ldd_parent") {
                newFolder = await CurrentFolder.getParent();
            } else {
                newFolder = await CurrentFolder.getFolder(folderName);
            }
            await FileToMove.moveTo(newFolder);
            setIsProcessing(false);
            setMoveFilePop({ show: false, doc: null });
            setErrorMsg(null);
            activityCenter.Add(`File '${FileToMove.name}' moved to '${newFolder.name}'.`, null, "success", null);
            FileDrive_Funcs.refreshFolder();
        } catch (e) {
            if (e === "conflict") {
                setErrorMsg(`A file with this name already exists. Please try a different name.`)
            } else {
                setErrorMsg(e);
            }
            setIsProcessing(false);
        }
    }

    const Indicators = useAppSelector((g: lddStateObject) => g.Indicators);
    useEffect(() => {
        FileDrive_Funcs.refreshFolder();
    }, [Indicators])

    if (Drive === undefined) {
        return <Loading />;
    }
    else return (
        <>
            {createNewFolderPop &&
                <Modal centered show={true} size="sm">
                    <Modal.Header>
                        <Modal.Title>New Folder</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isProcessing ?
                            <LoadingIcon showMsg={true} extraMsg="Creating Folder" inline={true} />
                            :
                            <Form noValidate autoComplete="off" validated={validated}
                                onSubmit={handleNewFolderSubmit}
                                onChange={handleNewFolderChange}>

                                {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}

                                <Form.Label>Folder Name</Form.Label>
                                <Form.Group controlId="createNewSubFolderName">
                                    <Form.Control required type="test" placeholder="enter folder name.." autoFocus />
                                    <Form.Control.Feedback type="invalid">Field cannot be empty.</Form.Control.Feedback>
                                </Form.Group>

                                <Button ref={FormSubmitRef} type="submit" style={{ display: "none" }}></Button>
                            </Form>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {!isProcessing &&
                            <>
                                <Button variant="text" onClick={(e) => { handleGenericReset(); setCreateNewFolderPop(false); }}>Cancel</Button>
                                <Button variant="primary" onClick={(e) => {
                                    if (FormSubmitRef.current) {
                                        FormSubmitRef.current.click()
                                    }
                                }}>Create</Button>
                            </>
                        }
                    </Modal.Footer>
                </Modal>
            }

            {uploadNewDocPop &&
                <Modal centered show={true} size="sm">
                    <Modal.Header>
                        <Modal.Title>Upload File to {FileDrive_Funcs.returnCurrentFolder()?.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isProcessing ?
                            <LoadingIcon showMsg={true} extraMsg="Uploading Document" inline={true} />
                            :
                            <Form noValidate validated={validated}
                                autoComplete="off"
                                onSubmit={uploadNewDocPopSubmit}
                                onChange={uploadNewDocPopChange}>
                                {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}
                                {uploadFile ?
                                    <>
                                        <Form.Label>File Name</Form.Label>
                                        <Form.Group as={InputGroup}>
                                            <Form.Control required type="text" id="fileToUploadName"
                                                placeholder="please enter file name.."
                                                autoFocus
                                                defaultValue={uploadFile.name.substring(0, uploadFile.name.indexOf("."))} />
                                            <Form.Control disabled id="fileToUploadExt"
                                                style={{ maxWidth: "64px" }}
                                                defaultValue={uploadFile.name.substring(uploadFile.name.indexOf(".") + 1)} />
                                            <Form.Control.Feedback type="invalid">Please fill in the new document name</Form.Control.Feedback>
                                        </Form.Group>
                                    </>
                                    :
                                    <Form.Group controlId="fileUploader">
                                        <Form.Control onChange={(e) => {
                                            if (((e.target) as any).files[0].type === "") {
                                                // dont allow folder
                                                e.preventDefault();
                                                e.stopPropagation();
                                            } else { setUploadFile(((e.target) as any).files[0]) }
                                        }}
                                            className="form-upload" type="file" />
                                        <Form.Control.Feedback type="invalid">Please select file to upload </Form.Control.Feedback>
                                    </Form.Group>
                                }

                                <Button type="submit" ref={FormSubmitRef} style={{ display: "none" }}></Button>
                            </Form>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {!isProcessing &&
                            <>
                                <Button variant="text" onClick={(e) => { handleUploadReset(); setUploadNewDocPop(false); }}>Cancel</Button>
                                {(uploadFile && validatedToProcess) ?
                                    <Button variant="primary" onClick={(e) => {
                                        if (FormSubmitRef.current) {
                                            FormSubmitRef.current.click()
                                        }
                                    }}>Upload{(FormSubmitRef.current?.checkValidity())}</Button>
                                    :
                                    <Button variant="secondary">Upload</Button>}
                            </>
                        }
                    </Modal.Footer>
                </Modal>
            }

            {uploadNewDocOverwritePop &&
                <Modal centered show={true} size="sm">
                    <Modal.Header>
                        <Modal.Title>Upload New File</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant="warning">You will lose your previous document content.</Alert>
                        <span>Document <b>{uploadFileFullName}</b> already exists, would you like to replace it?</span>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="text" onClick={(e) => { setUploadNewDocOverwritePop(false); setIsProcessing(false); handleUploadReset(); }}>Cancel</Button>
                        <Button variant="primary" onClick={(e) => { setUploadNewDocOverwritePop(false); ProcessUploadDocumentStep2(); }}>Replace</Button>
                    </Modal.Footer>
                </Modal>
            }

            {moveFilePop.show &&
                <Modal centered show={true} size="sm">
                    <Modal.Header>
                        <Modal.Title>Move "{moveFilePop.doc.name}"</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isProcessing ?
                            <LoadingIcon showMsg={true} extraMsg="Moving Document" inline={true} />
                            :
                            <Form noValidate onSubmit={handleMoveDocumentSubmit}>
                                {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}

                                <Form.Group>
                                    <Form.Label>Current Location</Form.Label>
                                    <Form.Control disabled required type="text" defaultValue={FileDrive_Funcs.returnCurrentFolder().name} />
                                </Form.Group>

                                <br />

                                <Form.Group controlId="moveFileNewFolder">
                                    <Form.Label>New Location</Form.Label>
                                    <Form.Select style={{ padding: '5px' }}>
                                        {!FileDrive_Funcs.isMainFolder() && <option value="ldd_parent">../ (parent folder)</option>}
                                        {FolderContent.filter((v: FileEntry | FolderEntry) => !v.isFile).map((folder: FolderEntry, index: number) => {
                                            return <option value={folder.name} key={`movedocOption${index}`}>{folder.name}</option>
                                        })}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">Please specify the folder to move this document to.</Form.Control.Feedback>
                                </Form.Group>

                                <Button type="submit" ref={FormSubmitRef} style={{ display: "none" }}></Button>
                            </Form>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {!isProcessing &&
                            <>
                                <Button variant="text" onClick={(e) => { handleGenericReset(); setMoveFilePop({ show: false, doc: null }) }}>Cancel</Button>
                                <Button variant="primary" onClick={(e) => {
                                    if (FormSubmitRef.current) {
                                        FormSubmitRef.current.click()
                                    }
                                }}>Move File</Button>
                            </>
                        }
                    </Modal.Footer>
                </Modal>}

            {copyFilePop.show &&
                <Modal centered show={true} size="sm">
                    <Modal.Header>
                        <Modal.Title>Copy "{copyFilePop.doc.name}"</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isProcessing ?
                            <LoadingIcon showMsg={true} extraMsg="Copying Document" inline={true} />
                            :
                            <Form noValidate validated={validated} autoComplete="off"
                                onSubmit={handleCopyDocumentSubmit}
                                onChange={(e) => {
                                    const filename = e.currentTarget.copyFileNewName.value;
                                    setValidated(!hasSpecialCharacter(filename, fileSpecialChars));
                                }}>

                                {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}

                                <Form.Label>File Name</Form.Label>
                                <Form.Group controlId="copyFileNewName">
                                    <Form.Control required type="test" placeholder="Please enter new document name" autoFocus defaultValue={copyFilePop.doc.justName} />
                                    <Form.Control.Feedback type="invalid">Please fill in the new document name</Form.Control.Feedback>
                                </Form.Group>
                                <Button type="submit" ref={FormSubmitRef} style={{ display: "none" }}></Button>
                            </Form>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {!isProcessing &&
                            <>
                                <Button variant="text" onClick={(e) => { handleGenericReset(); setCopyFilePop({ show: false, doc: null }) }}>Cancel</Button>
                                <Button variant="primary" onClick={(e) => {
                                    if (FormSubmitRef.current) {
                                        FormSubmitRef.current.click()
                                    }
                                }}>Copy</Button>
                            </>
                        }
                    </Modal.Footer>
                </Modal>
            }

            {renameModal.show &&
                <Modal centered show={true} size="sm">
                    <Modal.Header>
                        <Modal.Title>Rename "{(renameModal.doc === undefined) ? renameModal.folder.name : renameModal.doc.justName}"</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isProcessing ?
                            <LoadingIcon showMsg={true} extraMsg={`Renaming ${(renameModal.doc === undefined) ? "Folder" : "Document"}`} inline={true} />
                            :
                            <Form noValidate autoComplete="off" validated={validated}
                                onSubmit={(e) => { handleRenameSubmit(e, (renameModal.doc === undefined) ? "Folder" : "Document") }}
                                onChange={(e) => { handleRenameChange(e, (renameModal.doc === undefined) ? "Folder" : "Document") }}>

                                {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}

                                <Form.Label>{renameModal.doc === undefined ? "Folder" : "File"} Name</Form.Label>
                                <Form.Group as={InputGroup}>
                                    <Form.Control required type="test" id="renameNewName"
                                        placeholder={(renameModal.doc === undefined) ? renameModal.folder.name : renameModal.doc.justName}
                                        autoFocus />
                                    {renameModal.doc && <Form.Control disabled id="fileToUploadExt" style={{ maxWidth: "64px" }} defaultValue={renameModal.doc.ext} />}
                                    <Form.Control.Feedback type="invalid">Please enter a new {(renameModal.doc === undefined) ? "folder" : "document"} name</Form.Control.Feedback>
                                </Form.Group>

                                <Button type="submit" ref={FormSubmitRef} style={{ display: "none" }}></Button>
                            </Form>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {!isProcessing &&
                            <>
                                <Button variant="text" onClick={(e) => { handleGenericReset(); setRenameModal({ show: false, folder: null, doc: null }) }}>Cancel</Button>
                                <Button variant="primary" onClick={(e) => {
                                    if (FormSubmitRef.current) {
                                        FormSubmitRef.current.click()
                                    }
                                }}>Rename</Button>
                            </>}
                    </Modal.Footer>
                </Modal >
            }

            {deleteModal.show &&
                <Modal centered show={true}>
                    <Modal.Header>
                        <Modal.Title>Delete permanently?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isProcessing ?
                            <LoadingIcon showMsg={true} extraMsg={`Deleting ${(deleteModal.doc === undefined) ? "Folder" : "Document"}`} inline={true} />
                            :
                            <>
                                {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}
                                {deleteModal.doc === undefined ?
                                    <>
                                        <Alert variant="warning">Folders with contents can not be deleted. If this folder has contents, please delete them first.</Alert>
                                        <span>"{deleteModal.folder.name}" will be <b>permanently deleted</b> and you will not be able to restore it. Are you sure you want to delete it?</span>
                                    </> :
                                    <>
                                        <span>"{deleteModal.doc.name}" will be <b>permanently deleted</b> and you will not be able to restore it. Are you sure you want to delete it?</span>
                                    </>
                                }
                            </>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {!isProcessing &&
                            <>
                                <Button variant="text" onClick={(e) => { handleGenericReset(); setDeleteModal({ show: false, folder: null, doc: null }) }}>Cancel</Button>
                                <Button variant="primary" onClick={(e) => { ProccessDelete((deleteModal.doc === undefined) ? "Folder" : "Document"); }}>Delete</Button>
                            </>
                        }
                    </Modal.Footer>
                </Modal>
            }

            <div ref={ref}
                onDrop={onFileUploadDragEnd}
                onDragOver={(e) => { e.preventDefault(); return false; }}
                onDragEnter={(e) => { e.preventDefault(); return false; }}
                style={{ height: '100%' }}>
                <Card className="card-list" style={{ height: '100%' }}>
                    <Card.Header>
                        <i className="fa-regular fa-house" onClick={(e) => FileDrive_Funcs.getMainFolder()} title="Load main file folder"></i>
                        <i style={{ marginLeft: "5px", marginRight: "5px" }} className="fa-regular fa-arrow-up" title="Load parent folder" onClick={(e) => FileDrive_Funcs.getParentFolder()}></i>
                        <b>/ {FileDrive_Funcs.drawPath()}</b>

                        <div className="card-tools">
                            <Button variant="tool" title="Upload new document" onClick={(e) => { setUploadNewDocPop(true); }}><i className="fa-regular fa-arrow-up-to-line"></i> Upload</Button>
                            <Button variant="tool" title="Create new subfolder" onClick={(e) => { setCreateNewFolderPop(true); }}><i className="fa-regular fa-folder-plus"></i> Folder</Button>
                            <Button variant="primary" title="Refresh folder content" onClick={FileDrive_Funcs.refreshFolder}><i className="fa-regular fa-rotate"></i></Button>
                        </div>
                    </Card.Header>

                    <Card.Body>
                        <Table hover style={(isDriveLoading || FolderContent.length === 0) ? { height: "100%" } : {}}>
                            <thead>
                                <tr>
                                    <th style={{ width: '30px' }}></th>
                                    <th>Name</th>
                                    <th style={{ width: '40px' }}></th>
                                    <th style={{ width: '190px' }}>Modified</th>
                                    <th style={{ width: '130px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(isDriveLoading) ?
                                    <tr><td colSpan={5}><Loading /></td></tr>
                                    :
                                    (FolderContent.length === 0 ?
                                        <tr><td colSpan={5}><Alert variant="primary">No documents to display.</Alert></td></tr>
                                        :
                                        <>
                                            {FolderContent.filter((v: FileEntry | FolderEntry) => !v.isFile).map((folder: FolderEntry, index: number) => {
                                                return <FolderRow
                                                    key={`driveFolder${index}`}
                                                    folder={folder}
                                                    renameF={(setRenameModal)}
                                                    deleteF={(setDeleteModal)} />
                                            })}
                                            {FolderContent.filter((v: FileEntry | FolderEntry) => v.isFile).map((doc: FileEntry, index: number) => {
                                                return <DocumentRow
                                                    key={`driveDocument${index}`}
                                                    doc={doc}
                                                    renameD={setRenameModal}
                                                    deleteD={setDeleteModal}
                                                    moveD={setMoveFilePop}
                                                    copyD={setCopyFilePop}
                                                    portalState={portalState}
                                                    SessionID={SessionID}
                                                />
                                            })}
                                        </>
                                    )
                                }
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </div>
        </>
    )
})

function dispatch(arg0: any) {
    throw new Error("Function not implemented.");
}
